import _ from "underscore";

import Backbone from "pages/common/lib/backbone-helpers";
import Persistence from "pages/common/lib/grabyo-persistence";
import FormUtils from "util/form-utils";
import * as RoleUtils from "util/role-utils";
import Constants from "const/constants";
import { DEFAULT_STUDIO_USER_PROFILE_IMAGE_URL } from "const/images";
import { logout } from "api/utils";

import UploadEditor from "pages/editor/components/inputs/uploader/uploader.backbone";

import ConnectCollectionToStore from "../../lib/migration/connectCollectionToStore";
import { UserCredentialsStore } from "../store/usercredentials/user-credentials-store";
import SocialCredentialsCollection, {
    socialCredentialsModelConstructor
} from "../collection/socialcredentials";

var UserModel = Backbone.Model.extend({
    // Override Properties / Methods

    idAttribute: `userId`,
    urlRoot: `${Constants.DEFAULT_API_PATH}/users`,

    //TODO: we need to init this otherwise things don't work...
    socialCredentials: new SocialCredentialsCollection(),

    parse: function (response) {
        if (_.isArray(response.socialCredentials)) {
            // eslint-disable-next-line
            response.socialCredentials = response.socialCredentials;
        } else if (_.isArray(response.credentials)) {
            response.socialCredentials = response.credentials;
        } else if (_.isArray(response.data)) {
            response.socialCredentials = response.data;
        } else {
            response.socialCredentials = [];
        }

        response.socialCredentials = _.isArray(response.socialCredentials)
            ? response.socialCredentials
            : [];

        if (App.Utils.isMigrationEnabled(`userCredentials`)) {
            const userCredentialsStore = UserCredentialsStore.create({
                socialCredentials: response.socialCredentials
            });
            const LinkedUserCredentialsCollection = ConnectCollectionToStore({
                collection: SocialCredentialsCollection,
                store: userCredentialsStore,
                _model: null,
                polymorphicModel: (attrs) =>
                    socialCredentialsModelConstructor(attrs)
            });
            this.socialCredentials = new LinkedUserCredentialsCollection(
                response.socialCredentials,
                {
                    listenToNewCredentials: true,
                    user: this,
                    parse: true
                }
            );
        } else {
            this.socialCredentials = new SocialCredentialsCollection(
                response.socialCredentials,
                {
                    listenToNewCredentials: true,
                    user: this,
                    parse: true
                }
            );
        }

        // We are presuming the user only has a single role for now.
        response.role = Constants.USER_ROLES.USER;
        if (!_.isEmpty(response.roles)) {
            response.role = response.roles[0];
        }

        response.isAdmin = response.role === Constants.USER_ROLES.ADMIN;

        delete response.socialCredentials;
        delete response.credentials;

        return response;
    },

    regex: {
        // eslint-disable-next-line
        email: /^([\w-+\.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([\w-+]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\]?)$/
    },

    defaults: {
        profileImageUrl: DEFAULT_STUDIO_USER_PROFILE_IMAGE_URL,
        roles: [],
        connected: false,
        mfaStatus: `inactive`
    },

    constants: {
        STATUS: {
            ACTIVE: `active`,
            PENDING_INVITE: `pending_invite`,
            DEACTIVATED: `deactivated`,
            DELETED: `deleted`,
            LOCKED: `locked`
        }
    },

    schema: {
        email: {
            validators: [FormUtils.Validators.required(`email`), `email`],
            fieldClass: `grForm__field`
        },
        name: {
            validators: [FormUtils.Validators.required(`name`)],
            fieldClass: `grForm__field`
        },
        uploadedProfileImage: {
            type: UploadEditor,
            options: {
                type: `image`,
                compact: false,
                preview: true,
                multiple: false
            }
        }
    },

    initialize: function () {
        // We are presuming the user only has a single role for now.
        var role = Constants.USER_ROLES.USER;
        if (!_.isEmpty(this.get(`roles`))) {
            role = this.get(`roles`)[0];
        }
        this.set(`role`, role);

        var isAdmin = role === Constants.USER_ROLES.ADMIN;
        this.set(`isAdmin`, isAdmin);
    },

    getDisplayName: function () {
        var name = this.get(`email`);
        if (!name) {
            name = this.get(`name`);
        }
        return name;
    },

    toFormFormat: function () {
        var data = {
            id: this.id,
            userId: this.id,
            isAdmin: this.get(`isAdmin`),
            name: this.get(`name`),
            email: this.get(`email`),
            role: this.get(`role`),
            campaigns: [],
            streams: [],
            uploadedProfileImage: this.get(`profileImageUrl`),
            mfaStatus: this.get(`mfaStatus`),
            isMfaEnabled: this.get(`isMfaEnabled`)
        };

        return data;
    },

    logout,

    toggleGrabyoGalleryStatus: function () {
        var status = this.getGrabyoGalleryStatus();
        Persistence.PermStore.set(`galleryStatus`, (!status).toString());
    },

    getGrabyoGalleryStatus: function () {
        return Persistence.PermStore.get(`galleryStatus`) === `true`
            ? true
            : false;
    },

    hasRole: function (roleName) {
        return this.get(`role`).toUpperCase() === roleName.toUpperCase();
    },

    hasPermission: function (permission) {
        return RoleUtils.hasPermission(this.get(`role`), permission);
    },

    isActive: function () {
        return this.get(`status`) === this.constants.STATUS.ACTIVE;
    },

    isPending: function () {
        return this.get(`status`) === this.constants.STATUS.PENDING_INVITE;
    },

    isSuspended: function () {
        return this.get(`status`) === this.constants.STATUS.DEACTIVATED;
    },

    isDeleted: function () {
        return this.get(`status`) === this.constants.STATUS.DELETED;
    },

    isLocked: function () {
        return this.get(`status`) === this.constants.STATUS.LOCKED;
    },

    updateUser: function (options, success, fail) {
        var _this = this;
        var $promise = $.ajax({
            type: `PUT`,
            url: `${this.urlRoot}/${this.get(`userId`)}`,
            data: JSON.stringify(options),
            processData: false,
            contentType: `application/json`
        })
            .done(function (updateUser) {
                _this.set(updateUser);
                if (_.isFunction(success)) {
                    success.call(null, arguments);
                }
            })
            .fail(function () {
                if (_.isFunction(fail)) {
                    fail.call(null, arguments);
                }
            });

        return $promise;
    },

    getNameAndEmail: function () {
        return `${this.get(`name`)} (${this.get(`email`)})`;
    },

    toJSON: function () {
        var json = UserModel.__super__.toJSON.apply(this, arguments);
        if (this.socialCredentials) {
            json.socialCredentials = this.socialCredentials.toJSON();
        }
        return json;
    },

    isLoggedInUser: function () {
        return this.id === App.Models.user.id;
    }
});

export default UserModel;
